import React from 'react';

import {
  FTActionType,
  FTTransactionResult,
  isContractTypeCep18,
} from '@block-explorer/entities';
import { FlexRow, TableData, TableRow } from '@make-software/cspr-design';
import {
  BodyText,
  formatTimestamp,
  formatTimestampAge,
  Tooltip,
} from '@block-explorer/ui';
import { TableDataCep18TokenInfo } from '../table-data-cep18-token-info/table-data-cep18-token-info';
import {
  TableDataAccountInfo,
  TableDataCurrencyAmount,
} from '../../components';
import { deriveAccountInfo } from '../../utils/account';
import { TransactionHashLink } from '../hash-link/transaction-hash-link';
import TransactorInfo from '../transactor-info/transactor-info';
import Big from 'big.js';
import { PrecisionCase } from '../../utils/currency';
import { deriveFtTokenData } from '../../services';
import TableFormatFtAmountWithSymbol from '../table-data-currency-amount/table-format-ft-amount-with-symbol';
import { useTranslation } from 'react-i18next';

interface TokenTxsItemProps {
  data: FTTransactionResult;
  loading: boolean;
  showTokenInfo?: boolean;
  showCallerInfo?: boolean;
  isOutgoingAmount?: boolean;
  isTokenSymbolHasLink?: boolean;
}

export const TokenTxsItem = ({
  data,
  loading,
  showTokenInfo = true,
  showCallerInfo = true,
  isOutgoingAmount = false,
  isTokenSymbolHasLink = false,
}: TokenTxsItemProps) => {
  const { t } = useTranslation();
  const {
    deploy,
    deploy_hash,
    timestamp,
    amount,
    contract_package,
    ft_action_type_id,
    from_hash,
    from_public_key,
    from_type,
    from_account_info,
    from_centralized_account_info,
    from_cspr_name,
    caller_account_info,
    caller_centralized_account_info,
    to_hash,
    to_public_key,
    to_type,
    to_account_info,
    to_centralized_account_info,
    to_cspr_name,
    rate,
  } = data;

  const chargedAmount = Big(deploy?.payment_amount || '0')
    .minus(deploy?.refund_amount || '0')
    .toString();

  const cep18AmountData = deriveFtTokenData(
    contract_package
      ? {
          contractPackageInfo: contract_package,
        }
      : null
  );

  const callerAccountInfo = deriveAccountInfo(
    caller_account_info || caller_centralized_account_info
  );
  const isCep18 = isContractTypeCep18(contract_package);

  return (
    <TableRow key={deploy_hash}>
      {showTokenInfo && (
        <TableData>
          {cep18AmountData?.symbol && (
            <TableDataCep18TokenInfo
              data={contract_package}
              loading={loading}
              symbol={cep18AmountData?.symbol}
              nameSize={1}
            />
          )}
        </TableData>
      )}
      <TableData>
        <FlexRow align={'center'}>
          <Tooltip tooltipContent={deploy_hash} caption={t('Transaction Hash')}>
            <BodyText size={3} monotype>
              <TransactionHashLink isDetails hash={deploy_hash} />
            </BodyText>
          </Tooltip>
        </FlexRow>
      </TableData>
      {showCallerInfo && (
        <TableData>
          <TableDataAccountInfo
            logo={callerAccountInfo && callerAccountInfo?.logo}
            name={callerAccountInfo?.name}
            publicKey={deploy?.caller_public_key || deploy?.caller_hash}
            loading={loading}
            csprName={deploy?.caller_cspr_name}
            nameSize={1}
          />
        </TableData>
      )}
      <TableData>
        <Tooltip tooltipContent={formatTimestamp(timestamp)} monotype>
          <BodyText size={3} noWrap variation="darkGray">
            {formatTimestampAge(timestamp)}
          </BodyText>
        </Tooltip>
      </TableData>
      <TableData>
        <BodyText size={3} noWrap>
          {FTActionType[ft_action_type_id]}
        </BodyText>
      </TableData>
      <TableData>
        {(from_public_key || from_hash) && (
          <TransactorInfo
            type={from_type}
            publicKey={from_public_key}
            csprName={from_cspr_name}
            loading={loading}
            hash={from_hash}
            accountInfo={from_account_info || from_centralized_account_info}
            nameSize={1}
            withName
          />
        )}
      </TableData>
      <TableData>
        {(to_public_key || to_hash) && (
          <TransactorInfo
            type={to_type}
            publicKey={to_public_key}
            csprName={to_cspr_name}
            loading={loading}
            hash={to_hash}
            accountInfo={to_account_info || to_centralized_account_info}
            nameSize={1}
            withName
          />
        )}
      </TableData>
      <TableData align="right">
        {isTokenSymbolHasLink ? (
          <TableFormatFtAmountWithSymbol
            amount={amount}
            loading={loading}
            outgoingAmount={isOutgoingAmount}
            cep18AmountData={cep18AmountData}
            contractPackageData={contract_package}
            precisionCase={PrecisionCase.deployCost}
          />
        ) : (
          <TableDataCurrencyAmount
            amount={amount}
            outgoingAmount={isOutgoingAmount}
            loading={loading}
            cep18AmountData={isCep18 ? cep18AmountData : null}
            precisionCase={PrecisionCase.deployCost}
            shouldShortenAmount
          />
        )}
      </TableData>
      <TableData align="right">
        <TableDataCurrencyAmount
          amount={chargedAmount}
          tokenCurrencyRate={rate}
          loading={loading}
          precisionCase={PrecisionCase.deployCost}
        />
      </TableData>
    </TableRow>
  );
};

export default TokenTxsItem;
